import DeleteIcon from "@mui/icons-material/Delete";
import { Alert, Box, Grid, LinearProgress, Typography } from "@mui/material";
import IconButton from "@mui/material/IconButton";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import { styled } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import { useGetList, useRefresh } from "react-admin";
import sendRequest from "../../dataProvider/sendRequest";
import DownloadField from "../../unit/DownloadField";
import DownloadFieldLink from "../../unit/DownloadFieldLink";
import formatDate from "../../unit/formatDate";
import HmPageHeaderTitle from "../../unit/HmPageHeaderTitle";
import UploadFile from "../../unit/UploadFile";
import useNotify from "../../unit/useNotify";
import DocDeleteIcon from "./DocDeleteIcon";
const MainSection = styled("article")({
  paddingTop: "10px",
  paddingBottom: "48px",
  height: `calc(100vh - 120px - 60px - 110px - 50px)`,
  overflowY: "auto",
  backgroundColor: "#fff",
});

function getCreatedAtTime(value: unknown): number {
  if (!value || typeof value !== "string") {
    return 0;
  }

  // Some APIs return "YYYY-MM-DD HH:mm:ss"; normalize for reliable parsing.
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const parsed = new Date(normalized).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function buildDocumentSignature(documents: any[] | undefined): string {
  if (!Array.isArray(documents) || documents.length === 0) {
    return "";
  }

  return documents
    .map(
      (doc) =>
        `${doc?.id || "na"}-${doc?.fileCreatedAt || ""}-${doc?.filename || ""}`
    )
    .join("|");
}

const DocumentActionIcon = ({ ...props }) => {
  const { appId, docId, docType, type, docFileName, docList } = props;
  if (docList && docList.length > 0) {
    return (
      <>
        {docList.map(
          (item: { filename: any; filelink: unknown }, index: number) => (
            <DownloadFieldLink
              key={`doc_link_${appId}_${docId}_${
                item?.filename || "na"
              }_${String(item?.filelink || "na")}_${index}`}
              filename={appId + "_" + (item?.filename || docFileName)}
              filelink={item?.filelink}
            />
          )
        )}
      </>
    );
  } else {
    return (
      <>
        <DownloadField
          key={`doc_single_${appId}_${docId}_${docType}`}
          docId={docId}
          docType={docType ? docType : type}
          docFileName={docFileName}
          appId={appId}
        />
        {/* {type == "bbupload" ? (
          <IconButton edge="end" aria-label="" sx={{ color: "#1976d2" }}>
            <DocDeleteIcon docId={docId} type={docType} appId={appId} />
          </IconButton>
        ) : (
          <> </>
        )} */}
      </>
    );
  }
};
export default function ApplicationDocuments({ ...props }) {
  const { appId, step, substep } = props;
  // const reqUrl = 'applications/files/'+appId+'/all';
  const [documentData, setDocumentData] = useState<any[] | undefined>(
    undefined
  );
  const [isUploadSyncing, setIsUploadSyncing] = useState(false);
  const [pendingSignature, setPendingSignature] = useState("");
  const syncTimeoutRef = useRef<number | null>(null);
  const refresh = useRefresh();
  const reqUrl = `applications/stepfiles/${appId}/${step}/${substep}`;
  if (appId === "" || appId === undefined) {
    return <Typography>Loading data...</Typography>;
  }
  const { data, total, error } = useGetList(reqUrl);
  useEffect(() => {
    if (!Array.isArray(data)) {
      setDocumentData(data);
      return;
    }

    const sortedDocuments = [...data].sort((a, b) => {
      return (
        getCreatedAtTime(a?.fileCreatedAt) - getCreatedAtTime(b?.fileCreatedAt)
      );
    });

    setDocumentData(sortedDocuments);

    if (isUploadSyncing) {
      const currentSignature = buildDocumentSignature(sortedDocuments);
      if (currentSignature !== pendingSignature) {
        setIsUploadSyncing(false);
        if (syncTimeoutRef.current) {
          window.clearTimeout(syncTimeoutRef.current);
          syncTimeoutRef.current = null;
        }
      }
    }
  }, [appId, data, isUploadSyncing, pendingSignature]);

  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        window.clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  if (error) {
    return <Typography color="error">Error loading Documents data</Typography>;
  }
  if (!total) {
    return <Typography>Loading Application Documents Data</Typography>;
  }

  if (error) {
    return <Typography color="error">Error loading documents data</Typography>;
  }

  if (!documentData || documentData.length === 0) {
    return <Typography>Loading documents data ...</Typography>;
  }
  function standAppStep(step: string) {
    if (
      step == "To Notify " ||
      step == "Expired" ||
      step == "Countered" ||
      step == "Pre-Approved"
    ) {
      return "Notification ";
    } else if (step == "Funding ") {
      return "Disbursement ";
    } else if (step == "Hold " || step == "On Hold ") {
      return "New ";
    } else if (step == "Declined" || step == "Completed") {
      return "Closing ";
    }

    return step;
  }

  function refreshDocumentList() {
    const currentSignature = buildDocumentSignature(documentData);
    setPendingSignature(currentSignature);
    setIsUploadSyncing(true);

    if (syncTimeoutRef.current) {
      window.clearTimeout(syncTimeoutRef.current);
    }
    syncTimeoutRef.current = window.setTimeout(() => {
      setIsUploadSyncing(false);
      syncTimeoutRef.current = null;
    }, 12000);

    refresh();
  }
  return (
    <>
      <Grid container sx={{ width: "100%", p: 1 }}>
        <Grid item xs={12}>
          <HmPageHeaderTitle title={"Uploaded Documents"} />
        </Grid>
      </Grid>
      <MainSection
        id="mainsection-application-documents"
        className="app-summary-bodysection"
      >
        <Grid container>
          <Grid item xs={12}>
            <List
              sx={{
                width: "100%",
                paddingBottom: 0,
                maxWidth: "100%",
                bgcolor: "background.paper",
              }}
            >
              {documentData.map((doc, index) => (
                <ListItem
                  key={`doc_item_${doc?.id || "na"}_${
                    doc?.filename || "na"
                  }_${index}`}
                >
                  <ListItemAvatar>
                    <DocumentActionIcon
                      appId={appId}
                      docId={doc.id}
                      docType={doc.filetype}
                      type={doc.type}
                      docFileName={doc.filename2 ? doc.filename2 : doc.filename}
                      docList={doc?.filelist}
                    />
                  </ListItemAvatar>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <ListItemText
                      sx={{
                        width: "300px",
                        flex: "0 0 auto",
                        minWidth: 0,
                        margin: 0,
                        "& .MuiListItemText-primary": {
                          color: "black",
                          // fontSize: "0.875rem",
                          fontSize: "1rem",
                          //首字母大写
                          textTransform: "capitalize",
                        },
                        "& .MuiListItemText-secondary": {
                          fontSize: "0.75rem",
                          color: "gray",
                        },
                        "& .MuiTypography-body1": { color: "black" },
                        "& .MuiTypography-body2": { color: "black" },
                      }}
                      primary={doc.filename2 ? doc.filename2 : doc.filename}
                      secondary={
                        standAppStep(doc?.step ? doc?.step + " " : "") +
                        (doc.fileCreatedAt
                          ? formatDate(doc.fileCreatedAt)
                          : "") +
                        " by " +
                        doc?.fileCreatedBy
                      }
                    />
                    {doc.type == "bbupload" && (
                      <DocDeleteIcon
                        docId={doc.id}
                        type={doc.filetype}
                        appId={appId}
                        cb={() => {
                          //删除后刷新页面数据
                          refreshDocumentList();
                        }}
                      />
                    )}
                  </Box>
                </ListItem>
              ))}
            </List>
            <Box
              sx={{ width: "100%", maxWidth: 360, bgcolor: "background.paper" }}
            >
              {isUploadSyncing && (
                <Box sx={{ pb: 1 }}>
                  <Alert severity="info" sx={{ mb: 1 }}>
                    Upload completed. We are processing your file and it should
                    appear in the list shortly.
                  </Alert>
                  <LinearProgress />
                </Box>
              )}
              <UploadFile
                appId={appId}
                refreshDocumentList={refreshDocumentList}
              />
            </Box>
          </Grid>
        </Grid>
      </MainSection>
    </>
  );
}
